import { useMutation, useLazyQuery, useQuery } from '@apollo/client';
import {
  AsyncPropSelector,
  EventEmitter,
  EventEmitterWithIndex,
  getHtmlFromMarkDown,
  withErrorBoundary,
  COMMON_CONSTANTS,
  GLOBAL_AI_BOT_STATE,
  getTranslatedText,
  AI_AGENTS_CONSTANT_FEATURE_ID_MAP,
  getCurrentUserHook
} from '@nile/components';
import { useTicketTriageStore } from '@nile/components/src/Components/AI/store/ticketTriageStore.tsx';
import { dompurifier } from '@nile/components/src/Utils/helpers';
import {
  AccountClient,
  GET_ENTITY_NAMES_META,
  TicketClient
} from '@nile/graphql';
import { GET_TICKET_REQUESTER } from '@nile/graphql/src/Query/ticket.js';
import ClosureFields from '@nile/mangifera/src/Components/Ticket/ClosureFields';
import { SVGIcon } from '@superops/super-icons';
import { useIntl } from 'react-intl';
import { Button, TextStyle, Banner } from '@superops/ui-design-system';
import { cloneDeep, filter, forEach, isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import TriageFeedback from '@nile/components/src/Components/AI/components/AITicketTriage/TriageFeedback';
import AICopyAndPaste from '../AICopyAndPaste';
import DislikeCard from '../AIRecommendedSoutions/DislikeCard';
import { UPDATE_TRIAGE_TICKET } from '@nile/banyan/src/Graphql/Mutations/ai';
import GlobalAIBot from '@nile/components/src/Components/AI/components/GlobalAIBot.js';
import RandomText from '@nile/components/src/Components/AI/components/RandomText';
import { useAIStore } from '@nile/components/src/Components/AI/store/aiStore.tsx';
import { GET_TRIAGE_TICKET_DATA } from '@nile/banyan/src/Graphql/Query/ai';

const CustomLabel = ({ label }) => {
  return (
    <div className="flex fsize12 mw-fc w100">
      <TextStyle variation="aiSubdued" weight="Medium">
        {label} :
      </TextStyle>
    </div>
  );
};

const getDiff = (base: any, updated: any) => {
  return Object.keys(base).map((key) => {
    const oldValue = base[key];
    const newValue = updated[key];

    return {
      name: key,
      value:
        oldValue === newValue
          ? { old: oldValue }
          : { old: oldValue, new: newValue }
    };
  });
};

export const getSuggestedFields = ({
  baseForm,
  updatedForm,
  baseTechGroup,
  updatedTechGroup,
  baseFields,
  updatedFields
}) => {
  const output = [];
  let formData = {};
  let techGroupData = {};
  let fieldsDiff = {};
  if (baseForm?.formId && updatedForm?.formId) {
    const baseFormId = baseForm?.formId;
    const updatedFormId = updatedForm?.formId;
    formData = {
      name: 'form',
      value: {
        old: {
          id: baseFormId
        }
      }
    };
    if (baseFormId !== updatedFormId) {
      formData.value.new = {
        id: updatedFormId
      };
    }

    output.push(formData);
  }

  if (baseTechGroup?.groupId && updatedTechGroup?.groupId) {
    const baseTechGroupId = baseTechGroup?.groupId;
    const updatedTechGroupId = updatedTechGroup?.groupId;
    techGroupData = {
      name: 'techGroup',
      value: {
        old: {
          id: baseTechGroupId
        }
      }
    };
    if (baseTechGroupId !== updatedTechGroupId) {
      techGroupData.value.new = {
        id: updatedTechGroupId
      };
    }

    output.push(techGroupData);
  }

  if (baseFields && updatedFields) {
    fieldsDiff = getDiff(baseFields, updatedFields);
    if (!isEmpty(fieldsDiff)) {
      output.push(...fieldsDiff);
    }
  }

  if (!isEmpty(output)) {
    return output;
  }

  return undefined;
};

const AITicketTriage = (props) => {
  const { content } = props;
  const intl = useIntl();
  const currentUser = getCurrentUserHook();

  const [fieldsUpdating, setFieldsUpdating] = useState(false);
  const [showFormLoader, setShowFormLoader] = useState(false);
  const [loadingRiveState, setLoadingRiveState] = useState(null);

  const { data: responseEntityFields, loading, error } = useQuery(
    GET_ENTITY_NAMES_META,
    {
      client: AccountClient,
      variables: {
        entitiesName: ['Ticket']
      }
    }
  );

  const metaFields = responseEntityFields?.getEntitiesMeta?.[0]?.attributes;

  const getFieldLabel = (name) => {
    const field = metaFields?.find((field) => field?.name === name);
    if (field?.isCustom) {
      return field?.label;
    }
    return getTranslatedText(intl, field?.label);
  };

  const setShowRatingComponent = useAIStore((s) => s.setShowRatingComponent);

  const showRating = () => {
    setShowRatingComponent(true);
  };

  const updateLoadingRiveState = () => {
    if (loadingRiveState !== GLOBAL_AI_BOT_STATE.Search) {
      setTimeout(() => {
        setLoadingRiveState(GLOBAL_AI_BOT_STATE.Search);
      }, 100);
    }
  };

  const actions = useTicketTriageStore((state) => state.actions);
  const setActions = useTicketTriageStore((state) => state.setActions);

  const fieldsContext = useTicketTriageStore((state) => state.fieldsContext);
  const setFieldsContext = useTicketTriageStore(
    (state) => state.setFieldsContext
  );

  const reason = useTicketTriageStore((state) => state.reason);
  const setReason = useTicketTriageStore((state) => state.setReason);

  const initialized = useTicketTriageStore((state) => state.initialized);
  const setInitialized = useTicketTriageStore((state) => state.setInitialized);

  const isFieldsApproved = useTicketTriageStore(
    (state) => state.isFieldsApproved
  );
  const setIsFieldsApproved = useTicketTriageStore(
    (state) => state.setIsFieldsApproved
  );

  const viewReason = useTicketTriageStore((state) => state.viewReason);
  const setViewReason = useTicketTriageStore((state) => state.setViewReason);

  const replyResponse = useTicketTriageStore((state) => state.replyResponse);
  const setReplyResponse = useTicketTriageStore(
    (state) => state.setReplyResponse
  );

  const fieldsMeta = useTicketTriageStore((state) => state.fieldsMeta);
  const setFieldsMeta = useTicketTriageStore((state) => state.setFieldsMeta);

  const baseFieldsData = useTicketTriageStore((state) => state.baseFieldsData);
  const setBaseFieldsData = useTicketTriageStore(
    (state) => state.setBaseFieldsData
  );

  const baseTechGroupData = useTicketTriageStore(
    (state) => state.baseTechGroupData
  );
  const setBaseTechGroupData = useTicketTriageStore(
    (state) => state.setBaseTechGroupData
  );

  const setBaseFormData = useTicketTriageStore(
    (state) => state.setBaseFormData
  );

  const baseFormData = useTicketTriageStore((state) => state.baseFormData);

  const fieldsData = useTicketTriageStore((state) => state.fieldsData);
  const setFieldsData = useTicketTriageStore((state) => state.setFieldsData);

  const closureFieldsData = useTicketTriageStore(
    (state) => state.closureFieldsData
  );
  const setClosureFieldsData = useTicketTriageStore(
    (state) => state.setClosureFieldsData
  );

  const formData = useTicketTriageStore((state) => state.formData);
  const setFormData = useTicketTriageStore((state) => state.setFormData);

  const techGroupData = useTicketTriageStore((state) => state.techGroupData);
  const setTechGroupData = useTicketTriageStore(
    (state) => state.setTechGroupData
  );

  const isFieldsDisliked = useTicketTriageStore(
    (state) => state.isFieldsDisliked
  );
  const setIsFieldsDisliked = useTicketTriageStore(
    (state) => state.setIsFieldsDisliked
  );

  const isReplyDisliked = useTicketTriageStore(
    (state) => state.isReplyDisliked
  );
  const setIsReplyDisliked = useTicketTriageStore(
    (state) => state.setIsReplyDisliked
  );

  const toggleViewReason = () => {
    setViewReason(!viewReason);
  };

  const formatData = (actionsData) => {
    let selectedFormData;
    let selectedTechGroupData;
    let ticketFieldsData = {};

    actionsData?.forEach((action) => {
      if (action?.actionValueType === 'TICKET_REPLY') {
        setReplyResponse(action);
      }
      if (action?.actionValueType === 'TICKET_UPDATE') {
        const fieldName = action?.name;
        const fieldValue = action?.actionValueData;
        const formattedData = {
          [fieldName]: fieldValue
        };
        if (action?.name === 'form') {
          selectedFormData = {
            ...fieldValue,
            label: fieldValue?.name,
            value: fieldValue?.name
          };
        } else if (action?.name === 'techGroup') {
          selectedTechGroupData = {
            ...fieldValue,
            label: fieldValue?.name,
            value: fieldValue?.name
          };
        } else {
          ticketFieldsData = { ...ticketFieldsData, ...formattedData };
        }
      }
    });
    setBaseFieldsData(ticketFieldsData || null);
    setBaseTechGroupData(selectedTechGroupData || null);
    setBaseFormData(selectedFormData || null);
    setFieldsData(ticketFieldsData || null);
    setFormData(selectedFormData || null);
    setTechGroupData(selectedTechGroupData || null);
  };

  const replyContent = replyResponse?.actionValueData?.content;

  const formattedReplyContent = replyContent
    ? getHtmlFromMarkDown(replyContent)
    : null;

  const formattedReason = reason ? getHtmlFromMarkDown(reason) : '';

  const getChilds = (field) => {
    const result = [];
    function findChildren(parentColumnName) {
      if (!parentColumnName) return;
      const children = filter(
        fieldsMeta,
        (field) => field?.parentField?.columnName === parentColumnName
      );
      result.push(...children);
      forEach(children, (child) => findChildren(child?.columnName));
    }
    findChildren(field?.columnName);
    return result;
  };

  const onClosureFieldsChange = (field, value) => {
    const fieldName = field?.columnName;
    const isCustomField = field?.fieldCategory === 'CUSTOM';
    const updatedValue = value?.value;
    let updatedFieldsData = { ...fieldsData };
    let updatedClosureFieldsData = cloneDeep(closureFieldsData);
    const childs = getChilds(field);

    if (childs?.length > 0) {
      childs.forEach((child) => {
        const colName = child?.columnName;
        updatedFieldsData = { ...updatedFieldsData, [colName]: null };
        if (child?.fieldCategory === 'CUSTOM') {
          const closureCustomFields =
            updatedClosureFieldsData?.customFields || {};
          updatedClosureFieldsData = {
            ...updatedClosureFieldsData,
            customFields: {
              ...closureCustomFields,
              [colName]: null
            }
          };
        } else {
          updatedClosureFieldsData = {
            ...updatedClosureFieldsData,
            [colName]: null
          };
        }
      });
    }

    if (fieldName && updatedValue) {
      if (isCustomField) {
        let customFieldVariables = updatedClosureFieldsData?.customFields || {};
        customFieldVariables = {
          ...customFieldVariables,
          [fieldName]: updatedValue
        };
        updatedClosureFieldsData.customFields = customFieldVariables;
        updatedFieldsData[fieldName] = updatedValue;
      } else {
        updatedClosureFieldsData[fieldName] = updatedValue;
        updatedFieldsData[fieldName] = updatedValue;
      }

      setFieldsData(updatedFieldsData);
      setClosureFieldsData(updatedClosureFieldsData);
    }
  };

  const ticketId = content?.ticketId;

  const getIndividualTriageTicketData = async () => {
    const response = await TicketClient.query({
      query: GET_TRIAGE_TICKET_DATA,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
      variables: {
        id: ticketId
      }
    });
    return response?.data?.getTicket;
  };

  const onUpdateComplete = async () => {
    const updatedTicketData = await getIndividualTriageTicketData();
    formatData(updatedTicketData?.aiAgentActionData?.action);
    setActions(updatedTicketData?.aiAgentActionData?.action);
    setReason(updatedTicketData?.aiAgentActionData?.data?.reason);
    setFieldsContext(updatedTicketData?.aiAgentActionData?.data?.fieldsContext);
  };

  const [updateTicket] = useMutation(UPDATE_TRIAGE_TICKET, {
    client: TicketClient
  });

  const onFieldsUpdate = () => {
    let variables = {
      id: ticketId,
      ...closureFieldsData,
      handledByAIAgent: {
        featureId: AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE
      }
    };

    if (techGroupData?.groupId && techGroupData?.groupId) {
      variables = {
        ...variables,
        techGroup: {
          name: techGroupData?.name,
          groupId: techGroupData?.groupId
        }
      };
    }

    if (formData?.formId) {
      variables = {
        ...variables,
        form: { formId: formData?.formId }
      };
    }
    setFieldsUpdating(true);
    updateTicket({
      variables: variables
    })
      .then((res) => {
        if (res?.data?.updateTicket) {
          setIsFieldsApproved(true);
          EventEmitter.dispatch('refetchTicketFromAI');
          setFieldsUpdating(false);
          showRating();
          onUpdateComplete();
        }
      })
      .catch((err) => {
        console.log(err);
        setFieldsUpdating(false);
      });
  };

  const updateAllFields = (fieldsMetaData) => {
    if (!fieldsMeta) {
      setFieldsMeta(fieldsMetaData);
    }
  };

  const onTechGroupChange = (f, val) => {
    setTechGroupData({
      name: val?.name,
      label: val?.label,
      groupId: val?.groupId
    });
  };

  const onTicketFormChange = (f, val) => {
    const updatedFormData = val;
    setFormData(updatedFormData);
    setShowFormLoader(true);
    updateLoadingRiveState();

    const variables = {
      id: ticketId,
      form: { formId: updatedFormData?.formId },
      handledByAIAgent: {
        featureId: AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE
      }
    };

    updateTicket({
      variables: variables
    }).then((res) => {
      if (res?.data?.updateTicket) {
        setShowFormLoader(false);
        onUpdateComplete();
      }
    })``.catch((err) => {
      console.log(err);
      setShowFormLoader(false);
    });
  };

  const initializeClosureFields = () => {
    if (isEmpty(closureFieldsData)) {
      let initClosureFields = {};
      const fieldsDataArr = Object.entries(fieldsData);
      fieldsDataArr.forEach(([key, value]) => {
        const fieldMeta = fieldsMeta.find((meta) => meta.columnName === key);
        if (fieldMeta) {
          if (fieldMeta.fieldCategory === 'CUSTOM') {
            const currentCustomFields = initClosureFields?.customFields || {};
            initClosureFields = {
              ...initClosureFields,
              customFields: {
                ...currentCustomFields,
                [key]: value
              }
            };
          } else {
            initClosureFields = {
              ...initClosureFields,
              [key]: value
            };
          }
        }
      });
      setClosureFieldsData(initClosureFields);
    }
  };

  const [getTicketRequester] = useLazyQuery(GET_TICKET_REQUESTER, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });

  const [showEmptyRequesterBanner, setShowEmptyRequesterBanner] = useState(
    false
  );

  const [copied, setCopied] = useState(false);

  const [copyLoading, setCopyLoading] = useState(false);

  const copyTimeOutRef = React.useRef(null);

  const onCopy = () => {
    setCopyLoading(true);
    getTicketRequester({
      variables: {
        id: ticketId
      }
    })
      .then((res) => {
        if (res?.data?.getTicket?.requester) {
          EventEmitterWithIndex.dispatch('insertTriageTicketConversation', {
            content: formattedReplyContent
          });
          setCopied(true);
          showRating();
          setShowEmptyRequesterBanner(false);
        } else {
          setShowEmptyRequesterBanner(true);
        }
        setCopyLoading(false);
        if (copyTimeOutRef.current) {
          clearTimeout(copyTimeOutRef.current);
        }
        copyTimeOutRef.current = setTimeout(() => {
          setCopied(false);
        }, 2000);
      })
      .catch((err) => {
        console.log(err);
        setCopyLoading(false);
      });
  };

  const hideUpdateButton =
    isFieldsDisliked ||
    (isEmpty(closureFieldsData) && isEmpty(techGroupData) && isEmpty(formData));

  useEffect(() => {
    if (!initialized) {
      formatData(content?.aiAgentActionData?.action);
      setInitialized(true);
      setActions(content?.aiAgentActionData?.action);
      setReason(content?.aiAgentActionData?.data?.reason);
      setFieldsContext(content?.aiAgentActionData?.data?.fieldsContext);
    }
    return function cleanup() {
      if (copyTimeOutRef.current) {
        clearTimeout(copyTimeOutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(fieldsMeta)) {
      initializeClosureFields();
    }
  }, [fieldsMeta]);

  const fieldsLoadingMessage = [
    getTranslatedText(intl, 'msp.ai.ticket.triage.loading.message.1'),
    getTranslatedText(intl, 'msp.ai.ticket.triage.loading.message.2')
  ];

  const getFieldSuggestions = () => {
    const suggestionsData = getSuggestedFields({
      baseForm: baseFormData,
      updatedForm: formData,
      baseTechGroup: baseTechGroupData,
      updatedTechGroup: techGroupData,
      baseFields: baseFieldsData,
      updatedFields: fieldsData
    });
    return suggestionsData;
  };

  return (
    <div className="ai-content-container-max-height scroll ai-ticket-triage">
      <div className="mb10">
        <TextStyle variation="aiSubdued">
          <FormattedMessage id="onboarding.hey" /> {currentUser?.name}!
        </TextStyle>
      </div>

      {!isFieldsApproved &&
        (!isEmpty(fieldsMeta) ||
          !isEmpty(fieldsData) ||
          !isEmpty(techGroupData)) && (
          <div className="mb20">
            <TextStyle variation="aiSubdued">
              <FormattedMessage id="ai.ticket.triage.fields.update.heading" />
            </TextStyle>
          </div>
        )}

      {!isEmpty(formData) && !isFieldsDisliked && (
        <div className="mb10 ai-dark-bg p10 br10  ai-update-ticket-wrapper flex g10">
          <div className="mw-fc flex actr w100">
            <TextStyle variation="aiSubdued">
              <FormattedMessage id="ai.ticket.triage.form.match.heading" />
            </TextStyle>
          </div>
          <div className="w100">
            <AsyncPropSelector
              defaultValue={formData}
              hideTitle
              field={{
                name: 'ticket.form',
                label: 'Ticket form'
              }}
              updateSelection={onTicketFormChange}
              noPortalTarget
              isUiDesignSystem
            />
          </div>
        </div>
      )}

      <div
        id="loading-component"
        className={
          showFormLoader
            ? 'ai-loading-container ai-dark-bg br10 active flex col actr jctr w100 p20'
            : 'hiddenActions'
        }
      >
        <GlobalAIBot riveState={loadingRiveState} stateMachine="Monica_Bot" />
        <div className="mt-10 tcenter">
          <RandomText text={fieldsLoadingMessage} />
        </div>
      </div>

      {!showFormLoader && (
        <div
          className={showFormLoader ? 'hiddenActions' : ''}
          id="all-content-wrapper"
        >
          {isFieldsApproved && isEmpty(fieldsData) && isEmpty(techGroupData) ? (
            <div className="ai-dark-bg br10 active flex col actr jctr w100 p20 mb20">
              <div>
                <SVGIcon name="SuccessRound" size="xl" />
              </div>
              <div>
                <TextStyle variation="aiSubdued">
                  <FormattedMessage id="ai.ticket.fields.mapping.success.message" />
                </TextStyle>
              </div>
            </div>
          ) : (
            <>
              {(!isEmpty(fieldsData) || !isEmpty(techGroupData)) && (
                <div id="triage-fields">
                  <div className="mb10">
                    <TextStyle variation="aiSubdued">
                      <FormattedMessage id="ai.ticket.triage.fields.heading" />
                    </TextStyle>
                  </div>
                  <div className="mb10 ai-dark-bg p10 br10 ai-update-ticket-wrapper ">
                    {isFieldsDisliked ? (
                      <>
                        <DislikeCard
                          content="ai.ticket.triage.fields.dislike.message"
                          noAnimation
                        />
                      </>
                    ) : (
                      <>
                        {(!isEmpty(fieldsData) || !isEmpty(techGroupData)) && (
                          <div className="triage-ticket-card dark">
                            {!isEmpty(techGroupData) && (
                              <div className="flex g5 actr mb10">
                                <CustomLabel label="Technician group" />
                                <div className="w100 fsize12">
                                  <AsyncPropSelector
                                    defaultValue={techGroupData}
                                    hideTitle
                                    field={{
                                      name: 'techGroup.name',
                                      label: 'Technician group'
                                    }}
                                    updateSelection={onTechGroupChange}
                                    noPortalTarget
                                    isUiDesignSystem
                                  />
                                </div>
                              </div>
                            )}

                            {!isEmpty(fieldsData) && (
                              <div className="fields-wrapper actr">
                                <ClosureFields
                                  fields={fieldsData}
                                  onChange={onClosureFieldsChange}
                                  noPortalTarget
                                  isUiDesignSystem
                                  updateAllFields={updateAllFields}
                                  ticket={{
                                    ticketId,
                                    ...fieldsData
                                  }}
                                  customTextFieldUIProps={{
                                    inputVariant: 'default',
                                    bgTransparent: true
                                  }}
                                  datePickerPortalId="AI-Container-wrapper"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {!hideUpdateButton && (
            <>
              {!isFieldsApproved ? (
                <div className="mb40 flex actr fx-sb z0 position-relative cw-10">
                  <div>
                    <Button
                      onClick={onFieldsUpdate}
                      aiPrimaryFill
                      size="slim"
                      icon={
                        <SVGIcon name="SendWhite" size="sm" color="white" />
                      }
                      disabled={
                        isEmpty(closureFieldsData) &&
                        isEmpty(techGroupData) &&
                        isEmpty(formData)
                      }
                      loading={fieldsUpdating}
                    >
                      <FormattedMessage id="common.update" />
                    </Button>
                  </div>

                  <div>
                    <TriageFeedback
                      onFeedbackComplete={() => setIsFieldsDisliked(true)}
                      featureId={'14'}
                      sourceAction={['SUGGESTED_FIELDS']}
                      entity={'Ticket'}
                      entityId={ticketId}
                      getSuggestedFields={getFieldSuggestions}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex g10 actr h-28 mb20">
                  <div className="flex ignoreOverride">
                    <SVGIcon name="LoadingSuccessAnimated" size="sm" />
                  </div>
                  <div className="text-white flex">
                    <TextStyle>
                      <FormattedMessage id="ticket.details.runbook.approved" />
                    </TextStyle>
                  </div>
                </div>
              )}
            </>
          )}

          {formattedReplyContent && (
            <div id="triage-reply">
              <div className="mb10">
                <TextStyle variation="aiSubdued">
                  <FormattedMessage id="ai.ticket.triage.reply.content.heading" />
                </TextStyle>
              </div>
              {isReplyDisliked ? (
                <DislikeCard
                  content="ai.ticket.triage.reply.dislike.message"
                  noAnimation
                />
              ) : (
                <>
                  <TextStyle variation="aiSubdued">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: dompurifier(formattedReplyContent)
                      }}
                      className="mb10 ai-dark-bg p10 br10"
                    />
                  </TextStyle>

                  {showEmptyRequesterBanner && (
                    <div className="w100 cw-10 mb20">
                      <Banner status="warningDark" hideIcon padding="small">
                        <div className="flex actr g5">
                          <div className="flex">
                            <SVGIcon name="Restrict" color="#CCCCCC" />
                          </div>
                          <div className="flex">
                            <TextStyle variation="aiSubdued">
                              <FormattedMessage id="ai.ticket.triage.copy.requester.empty.warning" />
                            </TextStyle>
                          </div>
                        </div>
                      </Banner>
                    </div>
                  )}
                  <div className="mt10 flex actr fx-sb cw-10">
                    <div>
                      {copied ? (
                        <div className="flex g10 actr h-28">
                          <div className="flex ignoreOverride">
                            <SVGIcon name="LoadingSuccessAnimated" size="sm" />
                          </div>
                          <div className="text-white flex">
                            <TextStyle>
                              <FormattedMessage id="admin.customerSupportAccess.copied" />
                            </TextStyle>
                          </div>
                        </div>
                      ) : (
                        <Button
                          aiPrimaryFill
                          size="slim"
                          onClick={onCopy}
                          loading={copyLoading}
                          icon={<SVGIcon name="SendWhite" />}
                        >
                          <FormattedMessage id="ticket.aiFloater.copyAndPaste" />
                        </Button>
                      )}
                    </div>

                    <div>
                      <TriageFeedback
                        onFeedbackComplete={() => setIsReplyDisliked(true)}
                        featureId={'14'}
                        sourceAction={['SUGGESTION_CLARIFICATION']}
                        entity={'Ticket'}
                        entityId={ticketId}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {isReplyDisliked && isFieldsDisliked && (
            <div className="w100 mt20 cw-10">
              <Banner status="criticalDark" padding="small" hideIcon>
                <div className="flex g5 actr">
                  <div className="flex">
                    <SVGIcon name="Restrict" color="#CCCCCC" />
                  </div>
                  <div className="flex">
                    <TextStyle variation="aiSubdued">
                      <FormattedMessage id="ai.ticket.triage.stopped.working" />
                    </TextStyle>
                  </div>
                </div>
              </Banner>
            </div>
          )}

          {(formattedReason || !isEmpty(fieldsContext)) && (
            <>
              <div
                className="mb10 pointer mt40"
                onClick={toggleViewReason}
                role="button"
              >
                <div className="flex actr g5">
                  <div className="flex fsize12">
                    <TextStyle variation="aiSubdued">
                      <FormattedMessage id="ai.ticket.triage.view.reason" />
                    </TextStyle>
                  </div>
                  <div className="flex">
                    <SVGIcon
                      name={viewReason ? 'ArrowDown' : 'ArrowRight'}
                      size="sm"
                      color="white"
                    />
                  </div>
                </div>
              </div>

              {viewReason && (
                <div className="ai-dark-bg p10 br10">
                  {!isEmpty(fieldsContext) && (
                    <div className="mb5 fsize12">
                      {fieldsContext.map((field) => (
                        <div className="mb5">
                          <span className="mr-5">
                            <TextStyle variation="aiSubdued">
                              {getFieldLabel(field.label)} :
                            </TextStyle>
                          </span>
                          <span>
                            <TextStyle variation="aiSubdued">
                              {field.reason}
                            </TextStyle>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <TextStyle variation="aiSubdued">
                    <div
                      className="fsize12"
                      dangerouslySetInnerHTML={{
                        __html: dompurifier(formattedReason)
                      }}
                    />
                  </TextStyle>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(AITicketTriage);
